import React from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useNavigate } from 'react-router-dom'
import { OverlayTrigger, Popover, Tooltip } from 'react-bootstrap'
import { ListGroup } from 'react-bootstrap'
import { getIcon } from 'shared/icons'
import AppBadge from 'shared/badges/app-badge'
import { useSidebar } from './sidebar-context'

function SidebarLink({ path, url, title, icon, onNavigate, badge }) {
  const currentPath = window.location.pathname
  const navigate = useNavigate()
  const { isCollapsed, isMobile } = useSidebar()
  // Icons-only mode: collapsed on desktop. In the mobile drawer we always
  // show the full labels because there's room for them.
  const iconsOnly = isCollapsed && !isMobile

  const activeLink = () => {
    if (path === '/') {
      return currentPath === path
    } else if (path === 'dashboards') {
      return currentPath.includes('dashboards')
    } else if (currentPath.includes('dashboards')) {
      return false
    } else {
      // First path segment only: a substring test lit Vendors up on /monitoring/duplicate-vendors.
      return currentPath.split('/').filter(Boolean)[0] === path
    }
  }

  const activeDashboardLink = () => {
    if (path.includes('vendors')) {
      return currentPath.includes('dashboards/vendors')
    } else if (path.includes('vendor-remittance')) {
      return currentPath.includes('dashboards/vendor-remittance')
    } else if (path.includes('vendor')) {
      return currentPath.includes('dashboards/vendor-audit-log')
    }
    return currentPath.includes(path)
  }

  const handleNavigate = (e, target) => {
    e.preventDefault()
    if (onNavigate) onNavigate()
    navigate(`/${target}`)
  }

  const getUrlPath = (label) => {
    if (!label) return ''
    return label.replace(/\s+/g, '-').toLowerCase()
  }

  const isSubActive = (label) => {
    return currentPath.includes(getUrlPath(label))
      ? 'sub-active fw-normal'
      : 'fw-light'
  }

  const subOptions = []
  // Popover content with sub-options
  const popover = (
    <Popover
      id="popover-basic"
      className="custom-popover"
      arrowProps={{ style: { display: 'none' } }}
    >
      <Popover.Body>
        <ListGroup variant="flush">
          {subOptions.map((option, index) => (
            <ListGroup.Item
              key={index}
              action
              onClick={() => {
                if (onNavigate) onNavigate()
                navigate(`/${path}/${getUrlPath(option.title)}`)
              }}
              className={`nav-link d-flex align-items-center p-3 ${isSubActive(
                option.title
              )}`}
            >
              <img
                src={getIcon(option.abbreviation)}
                className="img-fluid mx-2"
                style={{ height: '20px' }}
                alt={option.title}
              />
              {/* SVG icon with right margin */}
              {option.title}
            </ListGroup.Item>
          ))}
        </ListGroup>
      </Popover.Body>
    </Popover>
  )

  // When collapsed, wrap the link in a tooltip so users can still discover
  // section names by hovering over the icon.
  const withTooltip = (element) => {
    if (!iconsOnly) return element
    return (
      <OverlayTrigger
        placement="right"
        delay={{ show: 200, hide: 0 }}
        overlay={<Tooltip id={`sidebar-tooltip-${path}`}>{title}</Tooltip>}
      >
        {element}
      </OverlayTrigger>
    )
  }

  if (subOptions.length > 0) {
    return (
      <li
        className={`nav-item ${
          activeDashboardLink() ? 'border-right border-primary' : ''
        }`}
      >
        <OverlayTrigger
          trigger={['click']}
          placement="right-start"
          overlay={popover}
        >
          <span
            role="button" // Adds button-like behavior
            tabIndex="0" // Makes it focusable
            title={iconsOnly ? title : undefined}
            aria-label={title}
            className={`nav-link ${
              activeDashboardLink() ? 'active' : 'fw-light'
            } ${iconsOnly ? 'nav-link-icon-only' : ''}`}
          >
            <FontAwesomeIcon
              className={activeDashboardLink() ? '' : 'feather'}
              style={{ minWidth: '30' }}
              icon={icon}
            />{' '}
            {!iconsOnly && title}
          </span>
        </OverlayTrigger>
      </li>
    )
  }

  return (
    <li
      className={`nav-item ${
        activeLink() ? 'border-right border-primary' : ''
      }`}
    >
      {withTooltip(
        <a
          className={`nav-link ${activeLink() ? 'active' : 'fw-light'} ${
            iconsOnly ? 'nav-link-icon-only' : ''
          }`}
          data-testid={`sidebar-link-${path}`}
          href={`/${path}`}
          onClick={(e) => handleNavigate(e, url)}
          aria-label={title}
        >
          <FontAwesomeIcon
            className={activeLink() ? '' : 'feather'}
            style={{ minWidth: '30' }}
            icon={icon}
          />{' '}
          {!iconsOnly && title}
          {!iconsOnly && badge && (
            <AppBadge variant="info" pill className="ms-2" style={{ fontSize: '0.6rem' }}>
              {badge}
            </AppBadge>
          )}
        </a>
      )}
    </li>
  )
}

export default SidebarLink
